import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff, Send, X } from 'lucide-react';
import { useSpeechToText } from '../../lib/useSpeechToText';
import { useLiveSpeech } from '../../lib/useLiveSpeech';

interface VoiceListenModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (text: string) => void;
  busy?: boolean;
}

type Phase = 'idle' | 'listening' | 'transcribing' | 'ready';

/**
 * مودال ویس:
 * - اگر Web Speech در دسترس باشد → متن زنده هنگام حرف زدن
 * - وگرنه → ضبط WAV و بعد از توقف تبدیل با Whisper
 * (هر دو با هم میکروفون را خراب می‌کنند؛ جدا اجرا می‌شوند)
 */
const VoiceListenModal: React.FC<VoiceListenModalProps> = ({ open, onClose, onConfirm, busy = false }) => {
  const [draft, setDraft] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [hint, setHint] = useState('');
  const [mode, setMode] = useState<'live' | 'whisper'>('whisper');
  const startedRef = useRef(false);

  const live = useLiveSpeech('fa-IR');

  const wav = useSpeechToText({
    baseText: '',
    onFinalTranscript: (text) => {
      const cleaned = text.trim();
      if (cleaned) {
        setDraft(cleaned);
        setPhase('ready');
        setHint('متن آماده است — در صورت نیاز ویرایش کنید و ارسال بزنید.');
      } else {
        setPhase('idle');
        setHint('متنی تشخیص داده نشد. دوباره ضبط کنید.');
      }
    },
  });

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      live.stop();
      live.reset();
      if (wav.listening) wav.stop();
      setDraft('');
      setPhase('idle');
      setHint('');
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;
    setDraft('');
    setPhase('listening');

    const w = window as unknown as {
      SpeechRecognition?: unknown;
      webkitSpeechRecognition?: unknown;
    };
    const canLive =
      window.isSecureContext &&
      Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);

    if (canLive) {
      setMode('live');
      setHint('در حال شنیدن… متن همین‌جا تایپ می‌شود. وقتی تمام شد دکمه قرمز را بزنید.');
      live.reset();
      void live.start();
    } else {
      setMode('whisper');
      setHint('این مرورگر پیش‌نمایش زنده ندارد. صحبت کنید، بعد دکمه قرمز را بزنید تا متن ساخته شود.');
      void wav.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (wav.error) {
      setHint(wav.error);
      setPhase('idle');
    }
  }, [open, wav.error]);

  useEffect(() => {
    if (!open) return;
    if (wav.transcribing) {
      setPhase('transcribing');
      setHint('در حال تبدیل صدا به متن...');
    }
  }, [open, wav.transcribing]);

  // اگر تشخیص زنده قطع شد/خطا داد → سوییچ به Whisper
  useEffect(() => {
    if (!open || mode !== 'live' || phase !== 'listening') return;
    if (live.error || (!live.listening && !live.supported && startedRef.current)) {
      // فقط وقتی واقعاً fail شده و هنوز متنی نداریم
      if (draft.trim() || live.liveText.trim()) return;
      setMode('whisper');
      setHint('تشخیص زنده در دسترس نیست. الان با سرور ضبط می‌شود — صحبت کنید و دکمه قرمز را بزنید.');
      live.stop();
      void wav.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, phase, live.error, live.listening, live.supported]);

  // همگام‌سازی متن زنده به draft هنگام گوش دادن
  useEffect(() => {
    if (!open || mode !== 'live' || phase !== 'listening') return;
    if (live.liveText.trim()) {
      setDraft(live.liveText.trim());
    }
  }, [open, mode, phase, live.liveText]);

  if (!open) return null;

  const isListening = phase === 'listening' || (mode === 'whisper' && wav.listening) || (mode === 'live' && live.listening);
  const livePreview = mode === 'live' ? live.liveText.trim() : '';
  const displayText = draft || livePreview;

  const handleToggle = () => {
    if (busy || phase === 'transcribing') return;

    if (isListening) {
      if (mode === 'live') {
        live.stop();
        const text = (live.liveText || live.finalText || draft).trim();
        setDraft(text);
        setPhase(text ? 'ready' : 'idle');
        setHint(text
          ? 'متن آماده است — در صورت نیاز ویرایش کنید و ارسال بزنید.'
          : 'متنی تشخیص داده نشد. دوباره ضبط کنید.');
        return;
      }

      // whisper
      setPhase('transcribing');
      setHint('در حال تبدیل صدا به متن...');
      wav.stop();
      return;
    }

    // شروع مجدد
    setDraft('');
    live.reset();
    setPhase('listening');
    if (mode === 'live' && live.supported) {
      setHint('در حال شنیدن… متن همین‌جا تایپ می‌شود.');
      void live.start();
    } else {
      setMode('whisper');
      setHint('صحبت کنید، بعد دکمه قرمز را بزنید تا متن ساخته شود.');
      void wav.start();
    }
  };

  const handleConfirm = () => {
    const text = draft.trim() || live.finalText.trim() || livePreview;
    if (!text || busy) return;
    live.stop();
    if (wav.listening) wav.stop();
    onConfirm(text);
  };

  const handleClose = () => {
    live.stop();
    if (wav.listening) wav.stop();
    onClose();
  };

  const canSend = Boolean(draft.trim()) && !busy && phase !== 'transcribing' && !isListening;
  const micSupported = mode === 'live' ? live.supported : wav.supported;

  return (
    <div
      className="fixed inset-0 z-[240] flex items-end sm:items-center justify-center bg-black/55 p-0 sm:p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-label="ضبط دستور صوتی"
    >
      <div className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[92dvh]">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold text-slate-900">دستور صوتی</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {mode === 'live'
                ? 'متن هنگام صحبت ظاهر می‌شود'
                : 'بعد از توقف دکمه قرمز، متن ساخته می‌شود'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="min-w-[44px] min-h-[44px] rounded-xl hover:bg-slate-100 flex items-center justify-center"
            aria-label="بستن"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 pt-5 pb-3 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={handleToggle}
            disabled={busy || phase === 'transcribing' || !micSupported}
            className={`relative h-20 w-20 rounded-full flex items-center justify-center transition-all disabled:opacity-40 ${
              isListening
                ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/30'
                : phase === 'transcribing'
                  ? 'bg-amber-500 text-white'
                  : 'bg-emerald-700 text-white shadow-md'
            }`}
            title={isListening ? 'توقف' : 'شروع ضبط'}
          >
            {isListening && (
              <span className="absolute inset-0 rounded-full bg-rose-500/40 animate-ping" aria-hidden />
            )}
            <span className="relative z-10">
              {phase === 'transcribing' ? (
                <Loader2 size={28} className="animate-spin" />
              ) : isListening ? (
                <MicOff size={28} />
              ) : (
                <Mic size={28} />
              )}
            </span>
          </button>
          <p
            className={`text-sm font-bold text-center px-2 ${
              isListening ? 'text-rose-700' : phase === 'transcribing' ? 'text-amber-700' : 'text-slate-600'
            }`}
          >
            {busy
              ? 'در حال ارسال به دستیار...'
              : phase === 'transcribing'
                ? 'در حال تبدیل صدا به متن...'
                : isListening
                  ? mode === 'live'
                    ? 'در حال شنیدن — صحبت کنید'
                    : 'در حال ضبط — صحبت کنید، بعد این دکمه را بزنید'
                  : 'برای ضبط دوباره، دکمه میکروفون را بزنید'}
          </p>
          {hint && <p className="text-[11px] text-slate-500 text-center leading-relaxed max-w-sm">{hint}</p>}
        </div>

        <div className="px-4 pb-3 flex-1 min-h-0">
          {phase === 'ready' || (draft && !isListening) ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              className="w-full min-h-[9rem] rounded-xl border border-emerald-200 bg-white px-4 py-3 text-base leading-relaxed text-slate-800 focus:ring-2 focus:ring-emerald-600/25 outline-none resize-y"
              dir="rtl"
              placeholder="متن تشخیص‌داده‌شده اینجا می‌آید..."
            />
          ) : (
            <div className="min-h-[9rem] max-h-[40vh] overflow-y-auto rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
              {!displayText ? (
                <p className="text-sm text-slate-500 text-center py-8 leading-relaxed">
                  {isListening
                    ? mode === 'live'
                      ? 'شروع کنید به صحبت… متن اینجا ظاهر می‌شود.'
                      : 'در حال ضبط صدا… متن بعد از زدن دکمه قرمز اینجا می‌آید.'
                    : 'متن اینجا نمایش داده می‌شود.'}
                  <br />
                  <span className="text-xs text-slate-400">مثال: کارگر ساده ۱۰ نفرروز با بهای ۲٫۵ میلیون اضافه کن</span>
                </p>
              ) : (
                <p className="text-base leading-relaxed text-slate-800 whitespace-pre-wrap">
                  {displayText}
                  {isListening && (
                    <span className="inline-block w-0.5 h-4 bg-emerald-700 mr-0.5 align-middle animate-pulse" />
                  )}
                </p>
              )}
            </div>
          )}
          {(wav.error || live.error) && (
            <p className="mt-2 text-xs text-rose-600">{wav.error || live.error}</p>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex gap-2 bg-white">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="flex-1 min-h-[44px] rounded-xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            انصراف
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSend}
            className="flex-1 min-h-[44px] rounded-xl bg-[#0a4d29] text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            ارسال به دستیار
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceListenModal;
